import { useModal } from "@/Contexts";
import { useConfig } from "@/api/hooks/useConfig";
import FormInput from "@/components/Form/FormInput";
import SelectInput from "@/components/Form/SelectInput";
import ModalContainer from "@/components/modalContainer";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useAccessParents } from "@/pages/infrastructure/functions";
import { useTrans } from "@/translations";

import { useMemo } from "react";
import { useForm } from "react-hook-form";

import { isEmpty, uniqBy } from "lodash";

const WEIGHT_REGEX =
  /^(?:\d*(?:\.0{1,2})?|(?:\d{1,2}|1[0-4]\d)(?:\.\d{1,2})?)$/;

export const EditEventModal: React.FC<{
  eventId: string;
  initialEventWeightKg: number;
  initialEventPointId: string;
  initialEventCustomerId: string;
  eventAccessParentId: string;
  editEvent: (payload: {
    id: string;
    weightKg?: number | undefined;
    pointId?: string | undefined;
  }) => Promise<void>;
}> = ({
  eventId,
  initialEventWeightKg,
  initialEventPointId,
  initialEventCustomerId,
  eventAccessParentId,
  editEvent,
}) => {
  const { t } = useTrans();
  const { hideModal } = useModal();
  const { ref: modalRef } = useModal();
  const { config: clientConfig } = useConfig();
  const { terminalConfig } = useConfigService();

  const { accessParents } = useAccessParents();

  const accessParent = useMemo(
    () => accessParents.find((ap) => ap.id === eventAccessParentId),
    [accessParents, eventAccessParentId],
  );

  const containers = useMemo(
    () => accessParent?.containers || [],
    [accessParent],
  );
  const customers = useMemo(
    () => accessParent?.customers || [],
    [accessParent],
  );

  const maxWeight =
    terminalConfig?.maxWeightPerThrow ||
    clientConfig?.maxWeightPerThrow ||
    t("common:defaultMaxWeightPerThrow");

  const formHandler = useForm({
    defaultValues: {
      weightKg: initialEventWeightKg,
      pointId: initialEventPointId,
      customerId: initialEventCustomerId,
    },
    mode: "all",
  });

  const {
    register,
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
  } = formHandler;

  const onSubmit = handleSubmit((eventFormData) => {
    const { weightKg, pointId, customerId } = eventFormData;

    const updateObject: any = {
      id: eventId,
    };

    if (Number(weightKg) !== initialEventWeightKg) {
      updateObject.weightKg = Number(weightKg);
    }

    if (pointId !== initialEventPointId) {
      updateObject.pointId = pointId;
    }

    if (customerId !== initialEventCustomerId) {
      updateObject.customerId = customerId;
    }

    if (
      weightKg !== initialEventWeightKg ||
      pointId !== initialEventPointId ||
      customerId !== initialEventCustomerId
    ) {
      editEvent(updateObject);
    }

    hideModal();
  });

  const accessPointOptions = useMemo(
    () =>
      uniqBy(containers, "wasteType.code").map((c) => ({
        label: `${c.wasteType.name} (${c.wasteType.code})`,
        value: c.id,
      })),
    [containers],
  );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        label: customer.name,
        value: customer.id,
      })),
    [customers],
  );

  const validWeightError = t("errors:mustBeValidEventWeight", { maxWeight });

  return (
    <ModalContainer
      title="actions:editRow"
      onConfirmText={"actions:save"}
      onConfirm={() => onSubmit()}
      onCancel={hideModal}
      onConfirmDisabled={!isEmpty(errors) || !isValid}
      className="w-3/4 sm:w-1/2 lg:w-1/3"
      showDiscardModal={isDirty}
    >
      <div className="mb-6 flex flex-col gap-y-5">
        <SelectInput
          label={t("formLabels:tenant")}
          name="customerId"
          options={customerOptions}
          control={control}
          menuPortalTarget={modalRef.current}
        />
        <FormInput
          name="weightKg"
          label={t("formLabels:weight")}
          register={register}
          error={errors?.weightKg}
          regexPattern={WEIGHT_REGEX}
          min={0}
          max={Number(maxWeight)}
          minMaxError={validWeightError}
          patternError={validWeightError}
          showErrorInTooltip
        />
        <SelectInput
          label={t("formLabels:wasteStream")}
          name="pointId"
          options={accessPointOptions}
          control={control}
          menuPortalTarget={modalRef.current}
        />
      </div>
    </ModalContainer>
  );
};
